import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Factory,
  TrendingUp,
  TrendingDown,
  Package,
  Calendar,
  MapPin
} from 'lucide-react';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { reportsService } from '../services/reportsService';

interface TransformationData {
  id: string;
  transformationRule: string;
  inputIngredient: string;
  outputProduct: string;
  inputQuantity: number;
  outputQuantity: number;
  expectedYield: number;
  actualYield: number;
  efficiency: number;
  waste: number;
  inputCost: number;
  outputValue: number;
  profitMargin: number;
  transformationDate: string;
  location: string;
  operator: string;
}

interface EfficiencySummary {
  totalTransformations: number;
  averageEfficiency: number;
  totalWaste: number;
  totalInputCost: number;
  totalOutputValue: number;
  overallProfitMargin: number;
  bestPerforming: string;
  worstPerforming: string;
}

const emptySummary: EfficiencySummary = {
  totalTransformations: 0,
  averageEfficiency: 0,
  totalWaste: 0,
  totalInputCost: 0,
  totalOutputValue: 0,
  overallProfitMargin: 0,
  bestPerforming: '-',
  worstPerforming: '-'
};

export const ProductionEfficiencyReport: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [transformationData, setTransformationData] = useState<TransformationData[]>([]);
  const [summary, setSummary] = useState<EfficiencySummary>(emptySummary);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setSelectedLocation(defaultLocationId);
    }
  }, [defaultLocationId]);

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await reportsService.getProductionEfficiencyReport(selectedLocation, {
        startDate: dateFrom,
        endDate: dateTo,
      });
      setTransformationData(data.transformations || []);
      setSummary(data.summary || emptySummary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el reporte');
      console.error('Error loading production efficiency report:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency >= 95) return 'text-green-600';
    if (efficiency >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEfficiencyBadge = (efficiency: number) => {
    if (efficiency >= 95) return 'bg-green-100 text-green-800';
    if (efficiency >= 85) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Header y Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Ubicación</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Seleccionar ubicación" />
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[140px]"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[140px]"
              />
            </div>

            <Button
              onClick={handleGenerateReport}
              disabled={loading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {loading ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resumen de KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Eficiencia Promedio</p>
                <p className={`text-2xl font-bold ${getEfficiencyColor(summary.averageEfficiency)}`}>
                  {summary.averageEfficiency.toFixed(1)}%
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Transformaciones</p>
                <p className="text-2xl font-bold text-blue-600">
                  {summary.totalTransformations}
                </p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contenido por pestañas */}
      <Tabs defaultValue="transformations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transformations">Detalle de Transformaciones</TabsTrigger>
          <TabsTrigger value="efficiency">Análisis de Eficiencia</TabsTrigger>
        </TabsList>

        <TabsContent value="transformations">
          <Card>
            <CardHeader>
              <CardTitle>Detalle de Transformaciones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Transformación
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Entrada
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Salida
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Rendimiento
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Eficiencia
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Operador
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {transformationData.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.transformationRule}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(item.transformationDate).toLocaleDateString('es-PE')}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900">
                          {item.inputQuantity.toFixed(1)} kg
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-900">
                          {item.outputQuantity.toFixed(1)} kg
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-sm text-gray-900">
                            {item.actualYield.toFixed(1)} kg / {item.expectedYield.toFixed(1)} kg
                          </div>
                          <div className="text-xs text-gray-500">Real / Esperado</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEfficiencyBadge(item.efficiency)}`}>
                            {item.efficiency.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.operator}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="efficiency">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Mejores Rendimientos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="font-medium text-green-800">{summary.bestPerforming}</p>
                      <p className="text-sm text-green-600">Mejor eficiencia del período</p>
                    </div>
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Oportunidades de Mejora</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <p className="font-medium text-red-800">{summary.worstPerforming}</p>
                      <p className="text-sm text-red-600">Requiere atención</p>
                    </div>
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
};